import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Box,
  IconButton,
  Divider
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { AnimalGroup, IndividualAnimal } from '../../../../types/animal';
import StatusChip from './StatusChip';
import { calculateAge } from '../../../../utils/animalUtils';

interface AnimalListProps {
  selectedGroup: AnimalGroup;
  currentAnimal: IndividualAnimal | null;
  handleViewAnimal: (animal: IndividualAnimal) => void;
  handleAnimalMenuClick: (event: React.MouseEvent<HTMLElement>, animal: IndividualAnimal) => void;
}

const AnimalList: React.FC<AnimalListProps> = ({
  selectedGroup,
  currentAnimal,
  handleViewAnimal,
  handleAnimalMenuClick
}) => {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {selectedGroup.type} List
        </Typography>
        <List>
          {selectedGroup.individuals.map((animal: any) => (
            <React.Fragment key={animal.id}>
              <ListItem
                key={animal.id}
                disablePadding
                secondaryAction={
                  <IconButton 
                    edge="end" 
                    aria-label="actions"
                    onClick={(e) => handleAnimalMenuClick(e, animal)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                }
              >
                <ListItemButton 
                  selected={currentAnimal?.id === animal.id}
                  onClick={() => handleViewAnimal(animal)}
                >
                  <ListItemAvatar>
                    <Avatar>{animal.name.charAt(0)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={
                      <Box display="flex" justifyContent="space-between">
                        <Typography>{animal.name}</Typography>
                        <StatusChip 
                          label={animal.status.charAt(0).toUpperCase() + animal.status.slice(1)} 
                          size="small" 
                          status={animal.status}
                        />
                      </Box>
                    }
                    primaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box>
                        <Typography variant="body2" display="block">
                          {animal.breed} • {animal.tagId || 'No Tag'} • {calculateAge(animal.birthDate)}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          <Typography variant="caption" color="secondary.main" fontWeight="medium">
                             Val: Ksh {(Number(animal.currentValue) || 0).toLocaleString()}
                          </Typography>
                          {((Number(animal.dailyYieldAvg) || 0) * (Number(animal.productMarketPrice) || 0) > 0) && (
                             <Typography variant="caption" color="success.main" fontWeight="medium">
                                • Rev: Ksh {Math.round((Number(animal.dailyYieldAvg) || 0) * (Number(animal.productMarketPrice) || 0) * 30).toLocaleString()}/mo
                             </Typography>
                          )}
                        </Box>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              </ListItem>
              <Divider variant="inset" component="li" />
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default AnimalList;
